import { useState, useReducer, Dispatch, SetStateAction } from 'react'
import { toast } from 'react-toastify'

import {
    AddPropertyForm,
    FormStatus,
    RemovePropertyForm,
} from '../models/Forms'
import { axiosInstance } from '../helpers'
import { IdentifiableObject } from '../models'

type FormAction =
    | { type: 'UPDATE_FIELD'; field: string; value: any }
    | {
          type: 'UPDATE_MANAGER'
          managerType: 'regional_manager' | 'property_manager'
          field: 'name' | 'email'
          value: string
      }
    | { type: 'ADD_OTHER_USER' }
    | {
          type: 'UPDATE_OTHER_USER'
          index: number
          field: 'name' | 'email'
          value: string
      }
    | { type: 'REMOVE_OTHER_USER'; index: number }
    | { type: 'RESET_FORM' }

const addPropertyFormReducer = (
    state: AddPropertyFormRequest,
    action: FormAction,
): AddPropertyFormRequest => {
    switch (action.type) {
        case 'UPDATE_FIELD':
            return {
                ...state,
                [action.field]: action.value,
            }
        case 'UPDATE_MANAGER':
            return {
                ...state,
                [action.managerType]: {
                    ...state[action.managerType],
                    [action.field]: action.value,
                },
            }
        case 'ADD_OTHER_USER':
            return {
                ...state,
                other_users: [
                    ...(state.other_users || []),
                    { name: '', email: '' },
                ],
            }
        case 'UPDATE_OTHER_USER':
            return {
                ...state,
                other_users:
                    state.other_users?.map((user, index) =>
                        index === action.index
                            ? { ...user, [action.field]: action.value }
                            : user,
                    ) || [],
            }
        case 'REMOVE_OTHER_USER':
            return {
                ...state,
                other_users:
                    state.other_users?.filter(
                        (_, index) => index !== action.index,
                    ) || [],
            }
        case 'RESET_FORM':
            return initialAddPropertyFormState
        default:
            return state
    }
}

export const useAddRemovePropertyForms = (organization: number | null) => {
    const [addPropertyFormList, setAddPropertyFormList] = useState<
        AddPropertyForm[]
    >([])
    const [removePropertyFormList, setRemovePropertyFormList] = useState<
        RemovePropertyForm[]
    >([])

    const [addPropertyFormState, addPropertyFormDispatch] = useReducer(
        addPropertyFormReducer,
        initialAddPropertyFormState,
    )

    const [loadingState, setLoadingState] = useState<LoadingState>(
        initialLoadingState,
    )

    const createAddPropertyForm = async (request: AddPropertyFormRequest) => {
        try {
            const res = await axiosInstance.post(
                'forms/add-property-form/',
                request,
            )
            const form: AddPropertyForm = res.data
            return form
        } catch (error: any) {
            toast.error(error.response.data.message)
            return Promise.reject(error)
        }
    }

    const createRemovePropertyForm = async (
        request: RemovePropertyFormRequest,
    ) => {
        try {
            const res = await axiosInstance.post(
                'forms/remove-properties-form/',
                request,
            )
            const form: RemovePropertyForm = res.data
            return form
        } catch (error: any) {
            toast.error(error.response.data.message)
            return Promise.reject(error)
        }
    }

    const getAddPropertyFormList = async () => {
        try {
            setLoadingState((prevState) => ({
                ...prevState,
                getAddPropertyFormList: true,
            }))
            const res = await axiosInstance.get('forms/add-property-form/')
            const forms: AddPropertyForm[] = res.data
            setAddPropertyFormList(forms)
            setLoadingState((prevState) => ({
                ...prevState,
                getAddPropertyFormList: false,
            }))
            return forms
        } catch (error: any) {
            toast.error(error.response.data.message)
            setLoadingState((prevState) => ({
                ...prevState,
                getAddPropertyFormList: false,
            }))
            return Promise.reject(error)
        }
    }

    const getRemovePropertyFormList = async () => {
        try {
            setLoadingState((prevState) => ({
                ...prevState,
                getRemovePropertyFormList: true,
            }))
            const res = await axiosInstance.get('forms/remove-properties-form/')
            const forms: RemovePropertyForm[] = res.data
            setRemovePropertyFormList(forms)
            setLoadingState((prevState) => ({
                ...prevState,
                getRemovePropertyFormList: false,
            }))
        } catch (error: any) {
            toast.error(error.response.data.message)
            setLoadingState((prevState) => ({
                ...prevState,
                getRemovePropertyFormList: false,
            }))
            return Promise.reject(error)
        }
    }

    const updateAddPropertyForm = async (request: UpdateFormRequest) => {
        try {
            const res = await axiosInstance.patch(
                `forms/add-property-form/${request.id}/`,
                request.body,
            )
            const updatedForm: AddPropertyForm = res.data
            updateAddPropertyFormState(updatedForm)
            return updatedForm
        } catch (error: any) {
            toast.error(error.response.data.message)
            return Promise.reject(error)
        }
    }

    const updateRemovePropertyForm = async (request: UpdateFormRequest) => {
        try {
            const res = await axiosInstance.patch(
                `forms/remove-properties-form/${request.id}/`,
                request.body,
            )
            const updatedForm: RemovePropertyForm = res.data
            updateRemovePropertyFormState(updatedForm)
            return updatedForm
        } catch (error: any) {
            toast.error(error.response.data.message)
            return Promise.reject(error)
        }
    }

    const getPendingFormCount = async () => {
        try {
            const res = await axiosInstance.get('forms/pending-form-count/')
            const count: number = res.data
            return count
        } catch (error: any) {
            toast.error(error.response.data.message)
            return Promise.reject(error)
        }
    }

    const updateAddPropertyFormState = (updatedValue: AddPropertyForm) => {
        _insertOrUpdateIdentifiable(updatedValue, setAddPropertyFormList)
    }

    const updateRemovePropertyFormState = (
        updatedValue: RemovePropertyForm,
    ) => {
        _insertOrUpdateIdentifiable(updatedValue, setRemovePropertyFormList)
    }

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: Dispatch<SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }

            if (!found) {
                safeOldValue.push(updatedValue)
            }

            return safeOldValue
        })
    }

    return {
        addPropertyFormState,
        addPropertyFormList,
        removePropertyFormList,
        loadingState,
        addPropertyFormDispatch,
        createAddPropertyForm,
        createRemovePropertyForm,
        getAddPropertyFormList,
        getRemovePropertyFormList,
        updateAddPropertyFormState,
        updateAddPropertyForm,
        updateRemovePropertyForm,
        updateRemovePropertyFormState,
        getPendingFormCount,
    }
}

export interface AddPropertyFormRequest {
    property_name: string
    organization: number
    address: string
    city: string
    state: string
    zip_code: string
    unit_count: number
    start_date: string
    regional_manager: {
        name: string
        email: string
    }
    property_manager?: {
        name: string
        email: string
    }
    other_users?: {
        name: string
        email: string
    }[]
    notes?: string | null
    billing_email?: string | null
}

export interface RemovePropertyFormRequest {
    companies: number[]
    organization: number
    reason: string
    termination_date?: string | null
}

export const initialAddPropertyFormState: AddPropertyFormRequest = {
    property_name: '',
    organization: 0,
    address: '',
    city: '',
    state: '',
    zip_code: '',
    unit_count: 0,
    start_date: '',
    regional_manager: {
        name: '',
        email: '',
    },
    property_manager: {
        name: '',
        email: '',
    },
    other_users: [],
    notes: null,
    billing_email: null,
}

export interface LoadingState {
    getAddPropertyFormList: boolean
    getRemovePropertyFormList: boolean
}

export const initialLoadingState: LoadingState = {
    getAddPropertyFormList: false,
    getRemovePropertyFormList: false,
}

export interface UpdateFormRequest {
    id: number
    body: {
        status: FormStatus
    }
}
